import { Location } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { filter, race, take, timer } from 'rxjs';

import { ToastService } from '../../../../services/toast.service';
import { CesButtonComponent } from '../../../ui/ces-buttons/ces-button.component';
import { CesButtonSecondaryComponent } from '../../../ui/ces-buttons/ces-button-secondary.component';

/**
 * Shared layout for the error pages (401/403/404): a centered split with the brand
 * image, a code + heading, a projected body, and a primary "home" action (plus an
 * optional "back"). Owns the single source of truth for the resilient navigation so
 * a failing route resolver surfaces a toast instead of an inert button.
 */
@Component({
  selector: 'ces-error-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, CesButtonComponent, CesButtonSecondaryComponent],
  host: { class: 'block h-full w-full' },
  templateUrl: './error-page.component.html',
  styleUrl: './error-page.component.scss',
})
export class ErrorPageComponent {
  // A back step's own navigation settles well within this window; bounding the recovery
  // listener stops a back target that fires no router event (a fragment change or a
  // replaceState'd history entry) from arming it for a later, unrelated navigation.
  private static readonly BACK_RECOVERY_WINDOW_MS = 4000;

  readonly code = input.required<string>();
  readonly heading = input.required<string>();
  readonly showBack = input(false);

  protected readonly titleId = computed(() => `ces-error-${this.code()}-title`);

  private readonly headingRef = viewChild<ElementRef<HTMLHeadingElement>>('errorHeading');
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  private backInFlight = false;

  constructor() {
    // Move focus to the heading on entry so assistive tech announces the error: Angular
    // does not move focus on a router-outlet swap, and a guard redirect lands here silently.
    afterNextRender(() => this.headingRef()?.nativeElement.focus());
  }

  goHome(): void {
    void this.navigateHome();
  }

  goBack(): void {
    if (this.backInFlight) {
      return;
    }
    if (window.history.length <= 1) {
      void this.navigateHome();
      return;
    }
    this.backInFlight = true;
    // Recover to home (with feedback) only if THIS back step's own navigation errors,
    // instead of stranding the user on a half-loaded page.
    race(
      this.router.events.pipe(
        filter(event => event instanceof NavigationError || event instanceof NavigationEnd || event instanceof NavigationCancel),
        take(1),
      ),
      timer(ErrorPageComponent.BACK_RECOVERY_WINDOW_MS),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(outcome => {
        this.backInFlight = false;
        if (outcome instanceof NavigationError) {
          void this.navigateHome();
        }
      });
    this.location.back();
  }

  // A failing route resolver (e.g. the home page's asset fetch when the API is unreachable)
  // rejects the navigation; surface it instead of leaving the button silently inert.
  private async navigateHome(): Promise<void> {
    try {
      if (await this.router.navigate(['/'])) {
        return;
      }
    } catch {
      // NavigationError falls through to the user-facing notice below.
    }
    this.toast.warning('pages.errors.navError');
  }
}
